// The data wiring behind the Lanes run-threads surfaces, extracted from
// ContextPanel so the parsing and query plumbing is unit-testable without
// mounting the trace detail panel.
import { chainTurnLineages, classifyThreadOpening, matchesSpawnPrompt, spawnPromptAgreement } from "./traceTurnLineage.js";
import { isSpawnExecutionName, spawnFromToolUse } from "./traceAgentSpawns.js";
import { turnFingerprints, compactionOpening } from "./traceContentBlame.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";

/**
 * The spawn prompts a conversation issued, per the corpus-evidenced rule set
 * (traceAgentSpawns.js): direct sub-agent assignments and inline workflow
 * scripts (which carry their child prompts verbatim). Sealed spawns
 * contribute nothing - there is no content to match. These are what the
 * threads panel matches sibling openings against to claim "spawned by this
 * trace".
 */
// Why the join found no conversation behind a spawn, in a sentence for the
// reader; `retry` is the surface's own advice when the record cannot date the
// spawn yet.
export function threadMissText(miss, { retry = "" } = {}) {
    const m = String(miss || "");
    if (m === "no-spawn-prompt") {
        return "This spawn's prompt wasn't captured (sealed or elided), so its conversation can't be matched by opening.";
    }
    if (m === "no-anchor") {
        return `No wall-clock anchor for this spawn's turn yet - the search window can't be placed.${retry ? ` ${retry}` : ""}`;
    }
    if (m.startsWith("pond-truncated")) {
        return "This endpoint was too busy around the spawn: the capped search may have dropped the thread's opening call before it could be matched.";
    }
    if (m === "no-token-carrier") {
        return "No other conversation carries this spawn's sealed task - the thread may not have been captured.";
    }
    if (m === "no-task-opening") {
        return "No conversation on this endpoint opens with this task's name - the thread may not have been captured.";
    }
    if (m.startsWith("no-opening-match")) {
        return `No conversation opening among ${m.split(":")[1] || "the"} nearby captures matches this spawn's prompt - the thread may not have been captured.`;
    }
    return "No captured call matches this spawn's window - the thread may not have been captured.";
}

export function extractSpawnPrompts(turns) {
    const out = [];
    for (const t of Array.isArray(turns) ? turns : []) {
        for (const b of t?.blocks || []) {
            if (b?.type !== "tool_use") continue;
            const spawn = spawnFromToolUse(b.name, b.input);
            if (spawn?.spawnPrompt) out.push(spawn.spawnPrompt);
        }
    }
    return out;
}

// Cache only supplied complete opening evidence, scoped to the auth session.
const _openingBodyCache = new Map();
const OPENING_BODY_CACHE_MAX = 128;
const RECONSTRUCT_CONCURRENCY = 4;

onAuthContextChange(() => _openingBodyCache.clear());

// Exact per-call openings are retired; what a capture itself shipped is not.
// A conversation's opener shipped its first messages, so the messages that
// capture was observed carrying are the opening the ponds match a spawn
// prompt against - read from the capture, never inferred from the
// conversation's current state. Supplied evidence still wins.
async function readOpeningConversation(row) {
    if (row.parsedConversation) return row.parsedConversation;
    const traceId = row.session_id ? String(row.session_id) : "";
    const sourceEventId = row.event_id ? String(row.event_id) : "";
    if (!traceId || !sourceEventId) return null;
    const at = parseServerTimestamp(row.event_time);
    const timeRange = Number.isFinite(at)
        ? { absolute: { start: new Date(at - DAY_MS).toISOString(), end: new Date(at + DAY_MS).toISOString() } }
        : { relative: { durationSeconds: String(90 * 86400) } };
    // Loaded on first use: the record reader brings the browser transport with
    // it, and the join only needs it once a pond has an opener to read.
    const { readObservedCapture } = await import("./conversationRead.js");
    return readObservedCapture({ traceId, sourceEventId, timeRange });
}

// Classify retained context through typed items. Projected captures with copied
// history continue an existing exchange; a body failure stays explicitly unknown.
export async function classifyOpeningRows(rows, { fetchConversation = readOpeningConversation } = {}) {
    const epoch = getAuthContextEpoch();
    const list = (rows || []).filter(r => r?.event_id).slice(0, OPENING_BODY_CACHE_MAX);
    const map = {}; let next = 0;
    const worker = async () => {
        for (;;) {
            const row = list[next++]; if (!row) return;
            const id = String(row.event_id); const key = `${epoch}:${id}`;
            if (_openingBodyCache.has(key)) { map[id] = _openingBodyCache.get(key); continue; }
            let parsed;
            try { parsed = await fetchConversation(row); } catch { parsed = null; }
            if (getAuthContextEpoch() !== epoch) return;
            const info = classifyThreadOpening(parsed?.turns);
            const compact = compactionOpening(parsed?.turns);
            const classification = compact ? { kind: "compaction", text: "", turnKeys: turnFingerprints(parsed?.turns), compactionFragments: compact.fragments }
                : { kind: parsed?.partialBody ? "unknown" : row.capture_projected && parsed?.turns?.some(t => t.usage === "copied") ? "mid" : info.kind,
                    text: info.text || "", turnKeys: turnFingerprints(parsed?.turns) };
            map[id] = classification;
            if (parsed?.turns?.length && !parsed.partialBody) {
                if (_openingBodyCache.size >= OPENING_BODY_CACHE_MAX) _openingBodyCache.delete(_openingBodyCache.keys().next().value);
                _openingBodyCache.set(key, classification);
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(RECONSTRUCT_CONCURRENCY, list.length) }, worker));
    return getAuthContextEpoch() === epoch ? map : {};
}

/**
 * Build the `threads` contract the Lanes view consumes. `queryFn` is the
 * dataClient `query` (injected so tests can spy on the exact calls). Keyed by
 * the turn's submission id for a capture, or by the session id for a session
 * tab. Returns null when neither key is present or there is no navigation
 * exit.
 */
export function buildLanesThreads({ submissionId = null, sessionId = null, currentTraceId, toolExecutions, spawnPrompts, onOpenTrace, queryFn, endpointId = null }) {
    if (!(submissionId || sessionId) || !onOpenTrace) return null;
    // A capture's threads are its turn's sibling calls; a session's are every
    // call of the conversation, so a spawn from any of its turns joins. The
    // rest of the wiring is key-agnostic: it chains, matches openings and
    // reads the child's conversation the same way from either pond.
    const resolve = submissionId
        ? () => queryFn("getTurnTraces", { submissionId }, { cache: true })
        : () => queryFn("getSessionTraces", { sessionId }, { cache: true });
    // Successful joins for THIS viewed trace: collapsing and re-expanding a
    // lane must not re-run the pond scan. Misses are NOT cached - they can
    // be transient (clock still settling, capture lag) and a retry is
    // exactly what the reader wants. Instance-scoped so a trace change
    // drops it with the wiring object.
    const joinCache = new Map();
    // One discovery probe per viewed trace: when the search surface refuses
    // (the typed-facts gate is off in prod), every later expand skips the
    // guaranteed-refusal round trip instead of rediscovering it per lane.
    // Instance-scoped like the join cache, so a trace change probes again.
    const searchState = { unavailable: false };
    return {
        resolve,
        resolveOpenings: async (ids) => {
            const res = await queryFn("getPromptMetadata", { eventIds: ids }, { cache: true });
            return classifyOpeningRows(res?.rows);
        },
        loadLaneTurns: (lane, opts) => loadLaneTurns({
            lane, executions: toolExecutions, resolve, queryFn, endpointId, sessionId,
            anchorMs: opts?.anchorMs ?? null, anchorSpan: opts?.anchorSpan ?? null,
            sessionFromMs: opts?.sessionFromMs ?? null, joinCache, searchState,
        }),
        spawnPrompts,
        onOpenTrace,
        executions: toolExecutions,
        currentTraceId,
    };
}

/**
 * The spawn->merge time window for one sub-agent lane, from the turn's
 * captured ToolCall executions: the Agent execution whose input carries this
 * lane's spawn prompt marks the MERGE instant, and the execution before it
 * bounds the spawn. Null when executions are absent or the spawn can't be
 * matched - the caller then shows the whole turn's thread list, labeled so.
 */
export function agentThreadBracket(lane, executions) {
    const list = (executions || [])
        .filter((e) => e && e.timestamp != null)
        .sort((a, b) => a.timestamp - b.timestamp);
    if (!list.length) return null;
    const probe = String(lane?.spawnPrompt || "").slice(0, 60).toLowerCase()
        || String(lane?.label || "").replace(/…$/, "").toLowerCase();
    if (!probe) return null;
    for (let i = 0; i < list.length; i++) {
        const e = list[i];
        if (!isSpawnExecutionName(e.name)) continue;
        if (!String(e.input || "").toLowerCase().includes(probe)) continue;
        return {
            from: (i > 0 ? list[i - 1].timestamp : e.timestamp - 60_000) - 2_000,
            to: e.timestamp + 2_000,
        };
    }
    return null;
}


const rowTimeMs = (row) => {
    const t = row?.time ?? row?.event_time ?? null;
    if (typeof t === "number") return t;
    const parsed = parseServerTimestamp(t);
    return Number.isFinite(parsed) ? parsed : null;
};

const rowIdOf = (row) => row?.traceId ?? row?.trace_id ?? null;

const MINUTE_MS = 60_000;
const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;
// Slack around record-dated bounds: a capture's clock and the child's first
// call sit seconds apart, so minutes cover the skew.
const SPAN_PAD_MS = 2 * MINUTE_MS;
// How long after its launch a child may still OPEN. Harnesses cap the
// background agents they run at once, so a launched child can wait for a
// slot - one observed live began ten minutes after the parent's ack.
const ASYNC_OPEN_REACH_MS = 6 * HOUR_MS;
const SYNC_OPEN_REACH_MS = 15 * MINUTE_MS;
// Reach either side of a clock ESTIMATE of the spawn turn. The search
// surface reads roughly proportionally to window width and refuses past a
// byte ceiling; a three-day window was refused live, twelve hours is well
// inside it, and the over-scan retry below halves from here if it is not.
const ESTIMATE_REACH_MS = 6 * HOUR_MS;
// One narrowing retry. Measured live, the surface refused a fifteen-minute
// window at the same ceiling as a three-day one, so a second refusal says
// the cost is not the window's and the trace stops asking (the memo below).
const OVER_SCAN_RETRIES = 1;
const POND_PAD_MS = 10 * MINUTE_MS;

/**
 * The window a lane's child conversation must have OPENED in, read off the
 * record the lanes axis draws. A record turn carries two clocks:
 *
 *   messageAt   the time the client wrote on the message, when it reports one
 *   capturedAt  the NEWEST capture that shipped the message
 *
 * The child opens after the message before its spawn was written and before
 * the message that merges its result was, so the written times bound it from
 * both sides. A capture time can only bound it from above: a client that
 * re-ships its history re-dates every message it re-sends, by days - one
 * record had every spawn, ack and merge re-dated to one later capture, and a
 * window built from those searched four minutes nowhere near the children.
 * So the written time is preferred on both sides, a capture time closes the
 * window from above when nothing after the merge was written with a time, and
 * a lower bound from capture times is trusted only while they still carry
 * order. A lane whose report has not merged yet (an async launch that was
 * only acknowledged) is open forward by the queueing reach.
 *
 * Null when the record cannot date the spawn. A full-echo client re-ships its
 * whole history every call, so a single distinct capture date across the axis
 * carries no order; and a merged lane whose message before the spawn, spawn
 * and merge all share one capture date was re-shipped the same way. The
 * caller falls back to its clock estimate.
 */
export function spawnWindowFromTurns(turns, lane) {
    const list = Array.isArray(turns) ? turns : [];
    const spawnTurn = lane?.turns?.[0];
    if (spawnTurn == null || !list.length) return null;
    const clock = (key) => (i) => {
        const t = list[i]?.[key];
        return Number.isFinite(t) ? t : null;
    };
    const written = clock("messageAt");
    const captured = clock("capturedAt");
    const nearest = (read, from, step) => {
        for (let i = from; i >= 0 && i < list.length; i += step) {
            const t = read(i);
            if (t != null) return t;
        }
        return null;
    };
    const merges = (lane.items || [])
        .filter((it) => it?.op?.kind === "merge" && it.turnIndex != null)
        .map((it) => it.turnIndex);
    const mergeTurn = merges.length ? Math.max(...merges) : null;

    let fromMs = nearest(written, spawnTurn - 1, -1);
    if (fromMs == null) {
        const distinct = new Set(list.map((t) => t?.capturedAt).filter((t) => Number.isFinite(t)));
        if (distinct.size < 2) return null;
        fromMs = nearest(captured, spawnTurn - 1, -1) ?? captured(spawnTurn);
        if (fromMs == null) return null;
        const mergeAt = mergeTurn == null ? null : nearest(captured, mergeTurn, 1);
        if (mergeAt != null && captured(spawnTurn) === fromMs && mergeAt === fromMs) return null;
    }
    let toMs = mergeTurn == null ? null : (nearest(written, mergeTurn, 1) ?? nearest(captured, mergeTurn, 1));
    const open = toMs == null;
    if (open) toMs = (nearest(written, spawnTurn, 1) ?? captured(spawnTurn) ?? fromMs) + ASYNC_OPEN_REACH_MS;
    return { fromMs: fromMs - SPAN_PAD_MS, toMs: Math.max(toMs, fromMs) + SPAN_PAD_MS, open };
}

// The window the join searches, best evidence first: the record's own dates
// around the spawn, the OS execution bracket, the caller's clock estimate,
// or the viewed run's own captures. `exact` says whether the child's origin
// is known to sit inside it (then the EARLIEST opening in it is the child)
// or the window merely surrounds a guess (then the NEAREST is).
function joinWindow({ anchorSpan, bracket, asyncSpawn, anchorMs, rows }) {
    if (Number.isFinite(anchorSpan?.fromMs) && Number.isFinite(anchorSpan?.toMs) && anchorSpan.toMs > anchorSpan.fromMs) {
        return { fromMs: anchorSpan.fromMs, toMs: anchorSpan.toMs, exact: true, nearestMs: anchorSpan.fromMs };
    }
    if (bracket) {
        return {
            fromMs: bracket.from - SPAN_PAD_MS,
            toMs: bracket.to + (asyncSpawn ? ASYNC_OPEN_REACH_MS : SYNC_OPEN_REACH_MS),
            exact: true, nearestMs: bracket.to,
        };
    }
    if (Number.isFinite(anchorMs)) {
        return { fromMs: anchorMs - ESTIMATE_REACH_MS, toMs: anchorMs + ESTIMATE_REACH_MS, exact: false, nearestMs: anchorMs };
    }
    // Last resort: the spawn happened somewhere in the viewed run, so its
    // captures bound it. Without this, an expand racing the clock fetch
    // silently skipped the search and paid the reconstruction pond.
    const times = rows.map(rowTimeMs).filter((t) => t != null);
    if (!times.length) return null;
    const lo = Math.min(...times);
    const hi = Math.max(...times);
    return { fromMs: lo - ESTIMATE_REACH_MS, toMs: hi + ESTIMATE_REACH_MS, exact: false, nearestMs: hi };
}

// A task lane's window: from the session's start - a follow-up addresses a
// child that may have opened at any point before it - to the lane's own time
// plus the queueing reach, which a spawn's child may still need to open.
function taskWindow({ anchorSpan, anchorMs, sessionFromMs }) {
    const at = Number.isFinite(anchorSpan?.toMs) ? anchorSpan.toMs : Number.isFinite(anchorMs) ? anchorMs : null;
    if (at == null) return null;
    const start = Number.isFinite(sessionFromMs) && sessionFromMs <= at ? sessionFromMs : at - ESTIMATE_REACH_MS;
    const fromMs = start - SPAN_PAD_MS;
    return { fromMs, toMs: at + ASYNC_OPEN_REACH_MS, exact: true, nearestMs: fromMs };
}

const pickHit = (hits, window) => hits.reduce((best, h) => {
    if (!best) return h;
    if (window.exact) return h.time < best.time ? h : best;
    return Math.abs(h.time - window.nearestMs) < Math.abs(best.time - window.nearestMs) ? h : best;
}, null);

/**
 * The sub-agent conversation behind one agent lane, as TURNS. The child is a
 * conversation of its own - a spawned agent's calls carry their own
 * conversation id, captured on the same endpoint - so the join names that
 * conversation and reads its record:
 *
 *   1. Search door (preferred): the conversation model indexes the child's
 *      OPENING - the spawn prompt, verbatim - as a role=user, first-chunk
 *      search row that carries the conversation id. One prefix search over
 *      the window the child must have opened in resolves the child; its
 *      record is then one page read. The window is the whole cost of the
 *      search, and an over-scan refusal narrows it rather than giving up.
 *   2. Opening-match ponds (search refused or missed): the run's sibling
 *      calls, then the endpoint's captures over the same window, chained
 *      into cache-chain lineages so only conversation OPENERS pay a read;
 *      the opener whose opening IS this lane's spawn prompt names the child.
 *   3. Spawn->merge window (last resort, when the spawn prompt was sealed):
 *      the bracket from the turn's captured executions, OPEN-ENDED for an
 *      async spawn - its bracket closes at the launch ack, not the merge.
 *
 * A matched capture reads as its conversation's RECORD (getSessionPage), the
 * same axis the parent is drawn on: every message the child's captures
 * shipped, not one call's view of them. A capture whose conversation the
 * metadata cannot name falls back to that capture's own opening. Returns
 * { turns, traceId, calls } on a join, where traceId is the conversation the
 * reader can open; every failure returns { miss: <gate> } naming exactly
 * which gate failed (no-spawn-prompt / no-endpoint / no-anchor /
 * no-opening-match:N / pond-truncated:N / body-unreadable) so the inset can
 * render the specific diagnosis.
 */
const OPENING_BATCH = 60;
const RESCUE_RECONSTRUCT_MAX = 12;
const LANE_JOIN_CACHE_MAX = 8;
// Conversations read to tell siblings' children apart, nearest first.
const SIBLING_READ_MAX = 6;

// Of the candidates whose opening matches the spawn prompt, the one whose
// opening runs longest with it (spawnPromptAgreement); a tie keeps the
// candidates' own order.
function bestSpawnMatch(candidates, textOf, prompt) {
    let best = null;
    for (const c of candidates) {
        const text = textOf(c);
        if (!matchesSpawnPrompt(text, [prompt])) continue;
        const run = spawnPromptAgreement(text, prompt);
        if (!best || run > best.run) best = { c, run };
    }
    return best ? best.c : null;
}

// The child among several conversations the opening search named. Sibling
// spawns cut from one template open with the same first line, so the search
// finds every sibling's child; each candidate's record is read (nearest
// first, in parallel, a handful at most) and the one whose opening runs
// longest with this lane's prompt is this lane's. Its record is the join.
async function siblingChild(hits, window, prompt, readRecord) {
    const bySession = new Map();
    for (const h of hits) {
        const key = String(h.sessionId).toLowerCase();
        if (!bySession.has(key)) bySession.set(key, []);
        bySession.get(key).push(h);
    }
    const heads = [...bySession.values()].map((hs) => pickHit(hs, window)).filter(Boolean)
        .sort(window.exact ? (a, b) => a.time - b.time : (a, b) => Math.abs(a.time - window.nearestMs) - Math.abs(b.time - window.nearestMs))
        .slice(0, SIBLING_READ_MAX);
    const read = await Promise.all(heads.map((h) => readRecord(String(h.sessionId), h.time).catch(() => null)));
    const records = read.filter((r) => r?.turns?.length);
    return { record: bestSpawnMatch(records, (r) => classifyThreadOpening(r.turns).text, prompt), read: records.length };
}

export async function loadLaneTurns(args) {
    const { lane, joinCache = null } = args;
    const cacheKey = `${lane?.id || ""}|${lane?.turns?.[0] ?? ""}|${String(lane?.spawnPrompt || lane?.taskName || "").slice(0, 80)}`;
    const cached = joinCache?.get(cacheKey);
    if (cached) return cached;
    // Cache the IN-FLIGHT promise, not the settled result: the inset's
    // effect can double-fire (dev StrictMode, fast collapse/expand), and
    // two concurrent joins each paid the full fetch bill before either
    // could publish. Misses and failures evict so a retry stays live.
    const p = loadLaneTurnsUncached(args);
    if (joinCache) {
        if (joinCache.size >= LANE_JOIN_CACHE_MAX) joinCache.delete(joinCache.keys().next().value);
        joinCache.set(cacheKey, p);
        p.then(
            (out) => { if (!out?.turns) joinCache.delete(cacheKey); },
            () => joinCache.delete(cacheKey),
        );
    }
    return p;
}

async function loadLaneTurnsUncached({ lane, executions, resolve, queryFn, endpointId = null, sessionId = null, anchorMs = null, anchorSpan = null, sessionFromMs = null, searchState = null, fetchConversation = readOpeningConversation }) {
    const res = await resolve();
    const rows = (Array.isArray(res) ? res : Array.isArray(res?.rows) ? res.rows : [])
        .filter((r) => rowIdOf(r) != null);
    const bracket = agentThreadBracket(lane, executions);
    const asyncSpawn = (lane?.items || []).some((it) => it?.op?.kind === "ack");
    const viewed = sessionId ? String(sessionId).toLowerCase() : null;
    // What the door searches for: the child's opening prompt, or - when the
    // harness sealed it - the task path a Codex child's opening header names.
    // A task lane may be a FOLLOW-UP to a child spawned long before, so its
    // window reaches back to the session's start; the earliest opening that
    // names the task in it is the child.
    const taskName = !lane?.spawnPrompt && lane?.taskName ? String(lane.taskName) : null;
    const window = taskName
        ? taskWindow({ anchorSpan, anchorMs, sessionFromMs })
        : joinWindow({ anchorSpan, bracket, asyncSpawn, anchorMs, rows });
    const term = lane?.spawnPrompt ? { prompt: lane.spawnPrompt } : taskName ? { taskName } : null;

    // A conversation's record, as the parent's axis reads it: the newest
    // page holds any sub-agent whole. `calls` counts the captures that
    // shipped it, the figure the request-centric read used to report.
    const readRecord = async (conversationId, sinceMs) => {
        const page = await queryFn("getSessionPage", { id: conversationId, sinceMs: Number.isFinite(sinceMs) ? sinceMs : null }, { cache: true });
        const turns = Array.isArray(page?.turns) ? page.turns : [];
        if (!turns.length) return null;
        const captures = new Set(turns.map((t) => t?.sourceEventId).filter(Boolean)).size;
        return { turns, traceId: conversationId, calls: captures || turns.length, ...(page.exhausted === false ? { truncated: true } : {}) };
    };
    // A matched capture as its conversation: the metadata names the
    // conversation (its record is the payload); a capture the metadata
    // cannot place, or whose record cannot be read - rejected or empty -
    // reads as its own opening, the request-centric fallback.
    const readThread = async (row) => {
        const traceId = rowIdOf(row);
        if (!traceId) return null;
        const refs = await queryFn("getPromptMetadata", { eventIds: [traceId] }, { cache: true });
        const refRow = (refs?.rows || [])[0];
        if (!refRow) return { miss: "body-unreadable" };
        if (refRow.session_id) {
            let record = null;
            try { record = await readRecord(String(refRow.session_id), rowTimeMs(row)); } catch { record = null; }
            if (record) return record;
        }
        let parsed;
        try { parsed = await fetchConversation(refRow); } catch { return { miss: "body-unreadable" }; }
        if (parsed?.partialBody) return { miss: "body-incomplete" };
        const turns = parsed?.turns;
        if (!Array.isArray(turns) || !turns.length) return { miss: "body-unreadable" };
        return { turns, traceId, calls: 1 };
    };

    let joined = null;
    // A search hit names the child, but its record can still fail to read
    // (transient, or a conversation the record has not normalized yet). The
    // ponds below can still reach the same child through its opener, whose
    // own capture stands in for an unreadable record; the flag keeps the
    // diagnosis honest if they cannot.
    let recordMiss = false;
    // A sealed Codex spawn's own token is the content join's needle: the one
    // high-entropy string the call and its child's opening share, so the
    // index's other carrier IS the child - exact where a task path is not
    // (a path repeats across follow-ups and sessions). The child is the
    // earliest carrier that received it: a later reader of the same text
    // cannot precede it. Children captured by agents that dropped the token
    // carry none and fall through to the header search.
    let tokenAsked = false;
    if (lane?.spawnToken && sessionId && window) {
        const found = await queryFn(
            "searchSealedTaskCarriers",
            { sessionId, token: lane.spawnToken, fromMs: window.fromMs, toMs: window.toMs },
            { cache: true },
        ).catch(() => null);
        tokenAsked = found?.ok === true;
        const child = (found?.rows || [])
            .filter((c) => c.ingested && Number.isFinite(c.time) && String(c.sessionId).toLowerCase() !== viewed)
            .sort((a, b) => a.time - b.time)[0];
        if (child) {
            let record = null;
            try { record = await readRecord(String(child.sessionId), child.time); } catch { record = null; }
            if (record) joined = record;
            else recordMiss = true;
        }
    }
    if (!joined && term && endpointId && window && !searchState?.unavailable) {
        let { fromMs, toMs } = window;
        for (let attempt = 0; attempt <= OVER_SCAN_RETRIES && !joined; attempt++) {
            const found = await queryFn(
                "searchSpawnOpenings",
                { ...term, endpointId, fromMs, toMs },
                { cache: true },
            ).catch(() => null);
            if (!found) break;
            if (found.ok === false) {
                // A REFUSAL is remembered for the trace (the typed-facts gate
                // off, RBAC): every later expand would meet it too. A
                // byte-ceiling rejection is first read as the window's cost -
                // narrow once and ask again, keeping the start when the child
                // is known to open at or after it. A narrowed window refused
                // as well is the surface's cost, and the trace stops paying
                // it: each refused search is seconds the pond below could
                // have spent finding the child.
                if (found.reason === "refused" && searchState) searchState.unavailable = true;
                if (found.reason !== "over-scan") break;
                if (attempt >= OVER_SCAN_RETRIES) { if (searchState) searchState.unavailable = true; break; }
                const half = (toMs - fromMs) / 2;
                if (window.exact) toMs = fromMs + half;
                else { fromMs = window.nearestMs - half / 2; toMs = window.nearestMs + half / 2; }
                continue;
            }
            const hits = (found.rows || []).filter((h) => h.role === "user" && h.chunkOrdinal === 0
                && h.sessionId && String(h.sessionId).toLowerCase() !== viewed
                && Number.isFinite(h.time) && h.time >= fromMs && h.time <= toMs);
            // The search matches the prompt's first line; siblings share it.
            if (term.prompt && new Set(hits.map((h) => String(h.sessionId).toLowerCase())).size > 1) {
                const sibling = await siblingChild(hits, window, term.prompt, readRecord);
                if (sibling.record) joined = sibling.record;
                else if (!sibling.read) recordMiss = true;
                break;
            }
            const hit = pickHit(hits, window);
            if (!hit) break;
            let record = null;
            try { record = await readRecord(String(hit.sessionId), hit.time); } catch { record = null; }
            if (record) joined = record;
            else recordMiss = true;
            break;
        }
    }
    if (!joined && lane?.spawnPrompt && rows.length) {
        const lineages = chainTurnLineages(rows)?.lineages || [];
        if (lineages.length) {
            // Classifying an opener reads its capture, so only the openers
            // nearest the window are worth it - the same cap the endpoint pond
            // keeps below.
            const openers = lineages.map((L) => L.rows?.[0]).filter((r) => rowIdOf(r) != null);
            const near = (r) => Math.abs((rowTimeMs(r) ?? Infinity) - (window?.nearestMs ?? 0));
            const ranked = window ? [...openers].sort((a, b) => near(a) - near(b)) : openers;
            const ids = [...new Set(ranked.slice(0, RESCUE_RECONSTRUCT_MAX).map(rowIdOf))];
            const refs = await queryFn("getPromptMetadata", { eventIds: ids }, { cache: true });
            const openings = await classifyOpeningRows(refs?.rows, { fetchConversation });
            const hit = bestSpawnMatch(lineages, (L) => {
                const first = rowIdOf(L.rows?.[0]);
                return first ? openings[String(first)]?.text : null;
            }, lane.spawnPrompt);
            if (hit) joined = await readThread(hit.rows[0]);
        }
    }
    // Why the join came up dry, precise enough to act on: which gate failed
    // is the whole diagnosis when this runs against a live trace.
    let miss = !term
        ? tokenAsked ? "no-token-carrier" : "no-spawn-prompt"
        : !endpointId
            ? "no-endpoint"
            : !window
                ? "no-anchor"
                : taskName
                    ? "no-task-opening"
                    : "no-opening-match";
    if (!joined?.turns && lane?.spawnPrompt && endpointId && window) {
        // The endpoint's captures over the same window, padded for the
        // pond's coarser clocks. Matching is ONE pass over the chain-deduped
        // candidates: the collapse below is what keeps that pass small.
        const ep = await queryFn(
            "getEndpointTraces",
            { endpointId, fromMs: window.fromMs - POND_PAD_MS, toMs: window.toMs + POND_PAD_MS },
            { cache: true },
        );
        // Only a conversation's FIRST call can carry its opening - so the
        // pond collapses to CHAIN OPENERS before any body is touched. The
        // chain, not the submission, is the conversation boundary: a
        // sidechain is captured under the SPAWNING run's submission, so the
        // child's opener sits mid-submission and only the cache-chain
        // arithmetic separates it from the parent's calls. Rows the chainer
        // cannot place (no token bands joined) stay candidates on their own,
        // trading a bigger candidate set for never dropping the opener.
        const timeAsc = (ep?.rows || [])
            .filter((r) => rowIdOf(r) != null && rowTimeMs(r) != null)
            .sort((a, b) => rowTimeMs(a) - rowTimeMs(b));
        const lineages = chainTurnLineages(timeAsc)?.lineages || [];
        const chained = new Set(lineages.flatMap((L) => L.rows.map(rowIdOf)));
        const openers = [
            ...lineages.map((L) => L.rows[0]),
            ...timeAsc.filter((r) => !chained.has(rowIdOf(r))),
        ];
        // Inside an exact window the child is the earliest opener at or
        // after its start; around an estimate it is the nearest one.
        const inside = (r) => (rowTimeMs(r) >= window.fromMs && rowTimeMs(r) <= window.toMs ? 0 : 1);
        const candidates = openers
            .filter((r) => rowIdOf(r) != null)
            .sort(window.exact
                ? (a, b) => inside(a) - inside(b) || rowTimeMs(a) - rowTimeMs(b)
                : (a, b) => Math.abs(rowTimeMs(a) - window.nearestMs) - Math.abs(rowTimeMs(b) - window.nearestMs))
            .slice(0, OPENING_BATCH);
        let first = null;
        const ids = [...new Set(candidates.map(rowIdOf))];
        if (ids.length) {
            const refs = await queryFn("getPromptMetadata", { eventIds: ids }, { cache: true });
            const rescueIds = new Set(candidates.slice(0, RESCUE_RECONSTRUCT_MAX).map(r => String(rowIdOf(r))));
            const openings = await classifyOpeningRows((refs?.rows || []).filter(r => rescueIds.has(String(r.event_id))), { fetchConversation });
            first = bestSpawnMatch(candidates, (r) => openings[String(rowIdOf(r))]?.text, lane.spawnPrompt);
        }
        // A capped pond fetch may have dropped the opener itself - say so
        // rather than reporting a clean no-match over a partial window.
        if (!first) miss = ep?.truncated ? `pond-truncated:${candidates.length}` : `no-opening-match:${candidates.length}`;
        if (first) joined = await readThread(first);
    }
    if (!joined?.turns && bracket && rows.length) {
        const to = asyncSpawn ? Infinity : bracket.to;
        const windowRows = rows
            .map((r) => ({ row: r, at: rowTimeMs(r) }))
            .filter((r) => r.at != null && r.at >= bracket.from && r.at <= to)
            .sort((a, b) => a.at - b.at);
        if (windowRows.length) {
            joined = await readThread(windowRows[windowRows.length - 1].row);
            if (joined?.turns) joined.calls = windowRows.length;
        }
    }
    if (!joined) return { miss: recordMiss ? "body-unreadable" : miss };
    return joined.turns ? joined : { miss: joined.miss || miss };
}
